/* 가이드 S3 저장소 — 초안(adm.guide 트리)을 official/guide/* 로 발행(I/O 전담).
   아티팩트 빌드는 guideArtifacts.js(순수). 발행 순서(DESIGN §G-7): content(변경분) →
   manifest → version.json(마지막=완전 발행 신호) → REMOVED content 정리.
   examples/repo.js 와 동형. s3.js 동결 시그니처만 사용. */
import { getJsonAnon, putObject, deleteObject } from '../../api/s3.js';
import { CODE_BUCKET_URL } from '../../config/env.js';
import { buildArtifacts, diffDraftVsPublished, contentKey } from './guideArtifacts.js';
import { uid } from '../../data/store.js';

export const VERSION_KEY = 'official/guide/version.json';
export const MANIFEST_KEY = 'official/guide/manifest.json';
/* 임시 저장본(작업본) — 발행 전 편집 트리를 서버에 보관. 앱은 version/manifest/content 만
   읽으므로 draft 는 사용자에게 노출되지 않는다. 단일 객체 last-writer-wins(운영자 소수 전제). */
export const DRAFT_KEY = 'official/guide/draft.json';
export { contentKey, diffDraftVsPublished };   // GuideAdmin 등이 guideRepo 에서 받아 쓰도록 재노출

const manifestUrl = (key) => (CODE_BUCKET_URL || '') + '/' + key;

/* 현재 발행본(manifest) 읽기. 없음(404/403) → 빈 트리. 형식 깨짐 → throw(orphan 쓰기 전 fail-fast). */
export async function loadPublished() {
  let json;
  try {
    json = await getJsonAnon(manifestUrl(MANIFEST_KEY));
  } catch (e) {
    if (e && (e.status === 404 || e.status === 403)) return { schema: 1, version: null, categories: [] };
    throw new Error(`발행본(manifest)을 읽지 못했습니다: ${e && e.message ? e.message : e}`);
  }
  if (!json || !Array.isArray(json.categories)) {
    throw new Error('manifest.json 형식이 올바르지 않습니다 (categories 배열 없음) — 덮어쓰기 전 중단합니다.');
  }
  return json;
}

/* 발행 본문 블록(계약 필드만)에 편집용 내부 id 부여 → BlockEditor 가 쓰는 모양으로 복원.
   id 는 발행 시 정규화로 다시 제거되므로 해시에 영향 없음. */
function rehydrateContent(content) {
  const out = {};
  for (const lang of Object.keys(content || {})) {
    const arr = Array.isArray(content[lang]) ? content[lang] : [];
    out[lang] = arr.map((b) => ({ id: uid('b'), ...(b || {}) }));
  }
  return out;
}

/* 서버 발행본(manifest + 각 content) → CMS 편집 트리 복원.
   어드민이 "현재 서버 상태"를 불러와 편집/추가 후 다시 발행하게 한다. 본문은 병렬 로드.
   서버가 비어있으면 빈 배열(첫 카테고리부터 추가). */
export async function loadGuideTree() {
  const pub = await loadPublished();
  const cats = [];
  const tasks = [];
  for (const c of pub.categories || []) {
    const cat = { id: c.id, color: c.color || '#0d9aa6', label: c.label || {}, big: [] };
    for (const b of c.big || []) {
      const big = { id: b.id, icon: b.icon || 'flag', label: b.label || {}, small: [] };
      for (const s of b.small || []) {
        const sm = { id: s.id, title: s.title || {}, content: {} };
        big.small.push(sm);
        tasks.push((async () => {
          try {
            const doc = await getJsonAnon(manifestUrl(s.content_key));
            sm.title = doc.title || sm.title;
            sm.content = rehydrateContent(doc.content);
          } catch (e) { /* 본문 누락 시 제목만 유지 */ }
        })());
      }
      cat.big.push(big);
    }
    cats.push(cat);
  }
  await Promise.all(tasks);
  return cats;
}

/* ---------- 임시 저장(draft) — 발행과 무관한 서버 작업본 ---------- */
/* 읽기 — 없음(404/403) → null. 형식 이상도 null(발행본으로 진행). */
export async function loadDraft() {
  let json;
  try {
    json = await getJsonAnon(manifestUrl(DRAFT_KEY));
  } catch (e) {
    if (e && (e.status === 404 || e.status === 403)) return null;
    throw new Error(`임시 저장본을 읽지 못했습니다: ${e && e.message ? e.message : e}`);
  }
  return json && Array.isArray(json.tree) ? json : null;   // { schema, saved_at, tree }
}

/* 쓰기 — 편집 트리 전체를 단일 PUT(원자적). 반환: saved_at(ISO). */
export async function saveDraft(getCredentials, tree) {
  const saved_at = new Date().toISOString();
  await putObject(getCredentials, DRAFT_KEY, JSON.stringify({ schema: 1, saved_at, tree }), 'application/json');
  return saved_at;
}

/* 삭제(발행 후·작업본 폐기) — best-effort. */
export async function deleteDraft(getCredentials) {
  try { await deleteObject(getCredentials, DRAFT_KEY); } catch (e) { /* best-effort */ }
}

/* 발행 — DESIGN §5. 반환: {version, published_at, changed:[ids], removed:[ids]}. */
export async function publish(getCredentials, { draftTree, langs, onStep }) {
  const step = (s) => { if (onStep) onStep(s); };

  step('발행본 확인 중…');
  const published = await loadPublished();          // 쓰기 전 read(RMW) — 변경/타임스탬프 기준
  const nowIso = new Date().toISOString();

  step('변경 사항 계산 중…');
  const { version, manifest, contentById, changed, removed } =
    await buildArtifacts(draftTree, langs, published, nowIso);

  // 1) NEW|CHANGED 작은 주제만 content PUT.
  let done = 0;
  for (const id of changed) {
    done++;
    step(`본문 업로드 중… (${done}/${changed.length})`);
    await putObject(getCredentials, contentKey(id), JSON.stringify(contentById[id]), 'application/json');
  }

  // 2) manifest → 3) version.json(마지막 = 완전 발행 신호).
  step('목록(manifest) 발행 중…');
  await putObject(getCredentials, MANIFEST_KEY, JSON.stringify(manifest), 'application/json');
  step('버전 발행 중…');
  await putObject(getCredentials, VERSION_KEY, JSON.stringify({ schema: 1, version, published_at: nowIso }), 'application/json');

  // 3.5) 발행 완료 → 작업본은 발행본과 동일해졌으므로 draft 정리(다음 로드 시 발행본 사용).
  await deleteDraft(getCredentials);

  // 4) REMOVED 정리 — manifest/version 갱신 후(더는 참조 없음) content best-effort 삭제.
  //    이미지는 다른 토픽이 공유할 수 있어 보존(dedup·소용량).
  for (const id of removed) {
    step('정리 중…');
    try { await deleteObject(getCredentials, contentKey(id)); } catch (e) { /* best-effort */ }
  }

  return { version, published_at: nowIso, changed, removed };
}
